/**
 * Vendor — MS26 per-vendor landing page (/vendors/<slug>).
 *
 * Public page that introduces a single Lembra vendor partner and
 * shows their full product catalogue. Built so:
 *   1. Vendors can share their own URL ("here's our Lembra page")
 *      as a partnership conversation opener.
 *   2. Search engines can index per-vendor SEO surfaces — each
 *      page has its own title, description, JSON-LD LocalBusiness,
 *      and is statically prerendered at build time.
 *   3. Customers landing here can browse that vendor's full
 *      catalogue and bridge into chat to brief a gift.
 *
 * Structure:
 *   - Minimal nav (Logo + back link)
 *   - Hero: eyebrow ("Lembra partner · category"), Newsreader
 *     italic-emphasis H1 (vendor name), DM Sans lede (description),
 *     and a hero photo from Lucky's Notion-curated set if available
 *   - Photo gallery (additional Notion photos if present)
 *   - Product grid (all SKUs from vendor_products via SkuCard)
 *   - "Send through Lembra" CTA → /chat with category prefilled
 *   - Footer (matches landing)
 *
 * Vendors without Lucky's Notion photos still get a page — they
 * fall back to the first product image as the hero, plus the
 * standard product grid below.
 */

import { useEffect, useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Helmet } from "@dr.pogodin/react-helmet";
import { ArrowRight, ArrowUp, AtSign, ExternalLink, MapPin } from "lucide-react";
import { Logo } from "@/components/lembra/Logo";
import { PageMeta } from "@/components/lembra/PageMeta";
import { SkuCard, type SkuCardData } from "@/components/chat/SkuCard";
import { ProductCheckoutModals } from "@/components/product-detail/ProductCheckoutModals";
import { useProductCheckoutFlow } from "@/hooks/useProductCheckoutFlow";
import { useAuth } from "@/hooks/useAuth";
import { useVendor, type VendorProductCard } from "@/hooks/queries";
import { getVendorPhotos } from "@/lib/vendorSlug";
import { getOrCreateGuestSessionId } from "@/lib/guestSession";

const displayFamily = "Newsreader, Georgia, serif";
const monoFamily =
  "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace";
const sansFamily = "DM Sans, sans-serif";

/* ─── Category → display label ───────────────────────────────────── */

const CATEGORY_LABEL: Record<string, string> = {
  cake: "Cake studio",
  hamper: "Hamper maker",
  florist: "Florist",
  spa: "Spa & wellness",
  workshop: "Workshop host",
  experience: "Experience curator",
  meal: "Caterer",
  voucher: "Voucher partner",
};

function categoryLabel(cat: string): string {
  return CATEGORY_LABEL[cat] ?? cat;
}

/* ─── Page ───────────────────────────────────────────────────────── */

export default function Vendor() {
  const { slug = "" } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const vendorQuery = useVendor(slug);
  const { session, loading: authLoading } = useAuth();
  const isAnon = !authLoading && !session;
  const guestSessionId = useMemo(
    () => (isAnon ? getOrCreateGuestSessionId() : null),
    [isAnon],
  );

  const checkout = useProductCheckoutFlow({ cameFrom: "vendor" });

  // Smooth-scroll to top on mount so a vendor link from elsewhere
  // (footer, MVS card) lands at the hero, not mid-page.
  useEffect(() => {
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  if (vendorQuery.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center" style={{ background: "#FAF6F0" }}>
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-navy border-t-transparent" />
      </div>
    );
  }

  if (vendorQuery.isError || !vendorQuery.data) {
    return (
      <>
        {/* MS185: tell crawlers this URL is a not-found state so it
            doesn't get indexed as a real vendor page. Title also
            shifts to a "not found" frame so any social share that
            still lands here doesn't preview as if the vendor exists. */}
        <Helmet>
          <title>Vendor not found. Lembra.</title>
          <meta name="robots" content="noindex,follow" />
        </Helmet>
      <div
        className="flex min-h-screen items-center justify-center px-6"
        style={{ background: "#FAF6F0" }}
      >
        <div className="text-center" style={{ maxWidth: 420 }}>
          <p
            style={{
              fontFamily: monoFamily,
              fontSize: 11,
              fontWeight: 500,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: "rgba(196,86,74,0.85)",
              marginBottom: 12,
            }}
          >
            Vendor not found
          </p>
          <h1
            style={{
              fontFamily: displayFamily,
              fontWeight: 500,
              fontSize: "1.85rem",
              lineHeight: 1.1,
              color: "#1D3461",
              margin: 0,
            }}
          >
            Nothing here{" "}
            <em style={{ fontStyle: "italic", color: "#C4564A" }}>yet</em>.
          </h1>
          <p
            className="mt-4 text-sm text-charcoal/65"
            style={{ fontFamily: sansFamily }}
          >
            This vendor isn't on Lembra yet. Browse the full catalogue or
            tell our concierge what you're after.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
            <Link
              to="/browse"
              className="inline-flex items-center gap-1.5 rounded-full border-2 border-navy/15 bg-white px-5 py-2 text-sm font-semibold text-navy hover:border-coral hover:text-coral focus:outline-none focus:ring-2 focus:ring-coral"
              style={{ fontFamily: sansFamily }}
            >
              Browse all vendors
            </Link>
            <Link
              to="/chat"
              className="inline-flex items-center gap-1.5 rounded-full bg-coral px-5 py-2 text-sm font-semibold text-white hover:bg-coral/85 focus:outline-none focus:ring-2 focus:ring-coral"
              style={{ fontFamily: sansFamily }}
            >
              Open the concierge
            </Link>
          </div>
        </div>
      </div>
      </>
    );
  }

  const { vendor, products } = vendorQuery.data;
  const photos = getVendorPhotos(slug);
  const heroPhoto = photos?.[0] ?? products[0]?.image_url ?? null;
  const galleryPhotos = photos?.slice(1) ?? [];
  const primaryCategory = products[0]?.vendor_category ?? "voucher";
  const productCount = products.length;
  // MS297: paused-vendor branch. When a vendor row stays `is_active=true`
  // but every SKU has `is_available=false` (Chéri's current state since
  // MS283 stopped surfacing their paused SKUs), the page used to render
  // a misleading "0 products on Lembra" hero with "Send through Lembra"
  // CTAs pointing at /chat?category=voucher — an order we couldn't
  // fulfil. Honest treatment: keep the vendor's hero metadata (so
  // bookmarks and backlinks land somewhere meaningful) but swap the
  // category-confidence lede + send CTAs for a friendly "not taking
  // orders right now → brief our concierge" frame, and noindex the
  // page so the paused state doesn't get cached in SERPs.
  const isPaused = productCount === 0;

  // Pick an emphasis word from the vendor name — last word usually
  // works ("Hampers", "Bakery", "Coffee"), falls back to first word
  // for single-word vendors ("Goldelucks", "Mabels").
  const nameWords = vendor.vendor_name.split(/\s+/);
  const emphasis = nameWords.length > 1 ? nameWords[nameWords.length - 1] : vendor.vendor_name;
  const beforeEmphasis = nameWords.length > 1 ? nameWords.slice(0, -1).join(" ") + " " : "";

  // MS179: JSON-LD structured data. The MS26 comment at the top of
  // this file already promised "JSON-LD LocalBusiness" but the code
  // never actually emitted it — fixing that here. Two schemas:
  //
  //   1. Organization (the vendor itself, name + url + sameAs links
  //      to their website + instagram if known)
  //   2. ItemList of Product entries (the vendor's full catalogue
  //      with name + image + price in AUD)
  //
  // Picked Organization over LocalBusiness because LocalBusiness
  // requires fields we don't have (street address, opening hours,
  // telephone) and Google rejects partial LocalBusiness blocks.
  // Organization is the next-best fit and is honored by the Google
  // Rich Results for sitelinks + knowledge-panel sourcing.
  //
  // Vendor pages are statically prerendered (scripts/prerender-blog.mjs)
  // so the JSON-LD ends up in the served HTML — crawlers read it
  // without needing JS.
  const canonicalUrl = `https://lembra.com.au/vendors/${slug}`;
  const sameAsLinks: string[] = [];
  if (vendor.website) sameAsLinks.push(vendor.website);
  if (vendor.instagram) {
    const handle = vendor.instagram.replace(/^@/, "").trim();
    if (handle) sameAsLinks.push(`https://instagram.com/${handle}`);
  }

  const orgLd: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: vendor.vendor_name,
    url: canonicalUrl,
    ...(sameAsLinks.length > 0 ? { sameAs: sameAsLinks } : {}),
    ...(vendor.delivery_suburbs
      ? { areaServed: { "@type": "Place", name: vendor.delivery_suburbs } }
      : {}),
  };

  const itemListLd: Record<string, unknown> | null =
    products.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: `Products from ${vendor.vendor_name} on Lembra`,
          numberOfItems: products.length,
          itemListElement: products.map((p, idx) => ({
            "@type": "ListItem",
            position: idx + 1,
            item: {
              "@type": "Product",
              name: p.name,
              ...(p.description ? { description: p.description } : {}),
              ...(p.image_url ? { image: p.image_url } : {}),
              brand: { "@type": "Brand", name: vendor.vendor_name },
              offers: {
                "@type": "Offer",
                price: p.price.toFixed(2),
                priceCurrency: "AUD",
                availability: "https://schema.org/InStock",
                url: canonicalUrl,
              },
            },
          })),
        }
      : null;

  // MS181: BreadcrumbList for the /vendors/<slug> trail. Helps Google
  // show the "Lembra › Vendors › <Name>" breadcrumb display in SERPs
  // instead of the raw URL. Three rungs match the actual nav hierarchy.
  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Lembra",
        item: "https://lembra.com.au",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Vendors",
        item: "https://lembra.com.au/vendors",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: vendor.vendor_name,
        item: canonicalUrl,
      },
    ],
  };

  return (
    <>
      <PageMeta
        title={
          isPaused
            ? `${vendor.vendor_name}: currently paused on Lembra.`
            : `${vendor.vendor_name} on Lembra. Workplace celebrations from Melbourne's best vendors.`
        }
        description={
          isPaused
            ? `${vendor.vendor_name} isn't taking orders through Lembra right now. Brief our concierge and we'll suggest a Melbourne maker with the same energy.`
            : `Send a ${categoryLabel(primaryCategory).toLowerCase()} gift from ${vendor.vendor_name} via Lembra. ${productCount} product${productCount === 1 ? "" : "s"}, real photos, real partner. Pay per celebration.`
        }
        path={`/vendors/${slug}`}
      />
      <Helmet>
        {/* MS297: noindex paused vendors so crawlers don't cache an
            empty catalogue state in SERPs. When the vendor returns,
            removing the SKU's is_available flag flips this back to
            indexable on the next crawl. */}
        {isPaused && <meta name="robots" content="noindex,follow" />}
        <script type="application/ld+json">{JSON.stringify(orgLd)}</script>
        {itemListLd && (
          <script type="application/ld+json">{JSON.stringify(itemListLd)}</script>
        )}
        <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>
      </Helmet>

      <div style={{ background: "#FAF6F0", minHeight: "100vh" }}>
        {/* ── Top nav (minimal) ── */}
        <header className="px-6 md:px-8 pt-6 pb-2">
          <div
            className="flex items-center justify-between"
            style={{ maxWidth: 1180, margin: "0 auto" }}
          >
            <Logo size="sm" />
            <Link
              to="/"
              className="text-sm font-medium text-navy/70 hover:text-coral"
              style={{ fontFamily: sansFamily }}
            >
              ← Back to lembra.com.au
            </Link>
          </div>
        </header>

        {/* ── Hero ── */}
        <section
          className="px-6 md:px-8"
          style={{
            paddingTop: "clamp(40px, 5vw, 64px)",
            paddingBottom: "clamp(40px, 5vw, 64px)",
          }}
        >
          <div
            className="grid gap-10 md:grid-cols-[1.1fr_1fr] md:gap-14 md:items-center"
            style={{ maxWidth: 1180, margin: "0 auto" }}
          >
            <div>
              <div
                style={{
                  fontFamily: monoFamily,
                  fontSize: 11,
                  fontWeight: 500,
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  color: "rgba(196,86,74,0.85)",
                  marginBottom: 12,
                }}
              >
                {isPaused
                  ? "Lembra partner · On pause"
                  : `Lembra partner · ${categoryLabel(primaryCategory)}`}
              </div>
              <h1
                style={{
                  fontFamily: displayFamily,
                  fontWeight: 500,
                  fontSize: "clamp(2.4rem, 5vw, 3.8rem)",
                  lineHeight: 1.05,
                  letterSpacing: "-0.022em",
                  color: "#1D3461",
                  margin: 0,
                  maxWidth: "16ch",
                }}
              >
                {beforeEmphasis}
                <em style={{ fontStyle: "italic", fontWeight: 500, color: "#C4564A" }}>
                  {emphasis}
                </em>
              </h1>

              {/* Meta row — suburb / IG / website. Quiet typographic
                  signal that this is a real vendor we partner with. */}
              <div
                className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2"
                style={{ fontFamily: sansFamily }}
              >
                {vendor.delivery_suburbs && (
                  <span className="inline-flex items-center gap-1.5 text-sm text-charcoal/70">
                    <MapPin size={13} strokeWidth={2} className="text-coral" />
                    {vendor.delivery_suburbs}
                  </span>
                )}
                {vendor.instagram && (
                  <a
                    href={`https://instagram.com/${vendor.instagram.replace(/^@/, "")}`}
                    target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm text-charcoal/70 hover:text-coral"
                  >
                    <AtSign size={13} strokeWidth={2} />
                    {vendor.instagram.replace(/^@/, "")}
                  </a>
                )}
                {vendor.website && (
                  <a
                    href={vendor.website}
                    target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm text-charcoal/70 hover:text-coral"
                  >
                    <ExternalLink size={13} strokeWidth={2} />
                    {vendor.website.replace(/^https?:\/\//, "").replace(/\/$/, "")}
                  </a>
                )}
              </div>

              <p
                style={{
                  fontFamily: sansFamily,
                  fontSize: 17,
                  lineHeight: 1.55,
                  color: "rgba(45,41,38,0.72)",
                  margin: "20px 0 0",
                  maxWidth: "52ch",
                }}
              >
                {isPaused ? (
                  <>
                    {vendor.vendor_name} isn't taking orders through Lembra at the
                    moment. Brief our concierge with who you're celebrating and we'll
                    suggest a Melbourne maker with the same energy.
                  </>
                ) : (
                  <>
                    {productCount} {primaryCategory === "voucher" ? "voucher" : "product"}
                    {productCount === 1 ? "" : "s"} on Lembra. Brief our concierge with who
                    you're celebrating and we'll handle vendor coordination, signed card,
                    and delivery.
                  </>
                )}
              </p>

              <div className="mt-7 flex flex-wrap items-center gap-3">
                {isPaused ? (
                  <Link
                    to="/chat"
                    className="inline-flex items-center gap-2 rounded-full bg-coral px-6 py-3 text-sm font-medium text-white transition-all hover:bg-coral/90 hover:-translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral focus-visible:ring-offset-2"
                    style={{
                      fontFamily: sansFamily,
                      boxShadow: "0 4px 12px -4px rgba(196,86,74,0.4)",
                    }}
                  >
                    Find something similar
                    <ArrowRight size={14} strokeWidth={2.5} />
                  </Link>
                ) : (
                  <Link
                    to={`/chat?category=${encodeURIComponent(primaryCategory)}&brief=${encodeURIComponent(`Send a gift from ${vendor.vendor_name}`)}`}
                    className="inline-flex items-center gap-2 rounded-full bg-coral px-6 py-3 text-sm font-medium text-white transition-all hover:bg-coral/90 hover:-translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral focus-visible:ring-offset-2"
                    style={{
                      fontFamily: sansFamily,
                      boxShadow: "0 4px 12px -4px rgba(196,86,74,0.4)",
                    }}
                  >
                    Send through Lembra
                    <ArrowUp size={14} strokeWidth={2.5} style={{ transform: "rotate(45deg)" }} />
                  </Link>
                )}
                <Link
                  to={isPaused ? "/vendors" : "/browse"}
                  className="inline-flex items-center gap-1.5 rounded-full border-2 border-navy/15 bg-white px-6 py-3 text-sm font-semibold text-navy hover:border-coral hover:text-coral focus:outline-none focus:ring-2 focus:ring-coral"
                  style={{ fontFamily: sansFamily }}
                >
                  {isPaused ? "Browse active vendors" : "Browse the full catalogue"}
                </Link>
              </div>
            </div>

            {/* Hero photo — square crop on desktop, full-width on mobile. */}
            {heroPhoto && (
              <div
                className="overflow-hidden rounded-2xl"
                style={{
                  border: "1px solid rgba(29,52,97,0.08)",
                  boxShadow: "0 24px 60px -30px rgba(29,52,97,0.4)",
                  aspectRatio: "5 / 4",
                  background: "rgba(45,41,38,0.04)",
                }}
              >
                <img
                  src={heroPhoto}
                  alt={`${vendor.vendor_name} hero shot`}
                  loading="eager"
                  decoding="async"
                  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                />
              </div>
            )}
          </div>
        </section>

        {/* ── Photo gallery (only if Lucky has additional Notion shots) ── */}
        {galleryPhotos.length > 0 && (
          <section
            className="px-6 md:px-8"
            style={{
              paddingTop: "clamp(24px, 3vw, 40px)",
              paddingBottom: "clamp(40px, 5vw, 64px)",
            }}
          >
            <div style={{ maxWidth: 1180, margin: "0 auto" }}>
              <div
                style={{
                  fontFamily: monoFamily,
                  fontSize: 11,
                  fontWeight: 500,
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  color: "rgba(196,86,74,0.85)",
                  marginBottom: 14,
                }}
              >
                More from the studio
              </div>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
                {galleryPhotos.map((src) => (
                  <div
                    key={src}
                    className="overflow-hidden rounded-xl"
                    style={{
                      border: "1px solid rgba(29,52,97,0.08)",
                      aspectRatio: "1 / 1",
                      background: "rgba(45,41,38,0.04)",
                    }}
                  >
                    <img
                      src={src}
                      alt={`${vendor.vendor_name} studio photo`}
                      loading="lazy"
                      decoding="async"
                      style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    />
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ── Products grid ── */}
        {/* MS297: paused vendors skip the catalogue + bottom-CTA sections
            entirely. The hero already framed the pause + offered the
            concierge handoff; rendering an empty "Everything X makes,
            on Lembra" heading followed by a "Ready to send something
            from them?" CTA was the exact dishonesty we're fixing. */}
        {!isPaused && (
          <section
            className="px-6 md:px-8"
            style={{
              background: "white",
              paddingTop: "clamp(56px, 7vw, 88px)",
              paddingBottom: "clamp(56px, 7vw, 88px)",
              borderTop: "1px solid rgba(29,52,97,0.06)",
            }}
          >
            <div style={{ maxWidth: 1180, margin: "0 auto" }}>
              <div className="mb-8 flex items-end justify-between gap-6">
                <div>
                  <div
                    style={{
                      fontFamily: monoFamily,
                      fontSize: 11,
                      fontWeight: 500,
                      letterSpacing: "0.18em",
                      textTransform: "uppercase",
                      color: "rgba(196,86,74,0.85)",
                      marginBottom: 10,
                    }}
                  >
                    Catalogue
                  </div>
                  <h2
                    style={{
                      fontFamily: displayFamily,
                      fontWeight: 500,
                      fontSize: "clamp(1.85rem, 3.4vw, 2.6rem)",
                      lineHeight: 1.08,
                      letterSpacing: "-0.018em",
                      color: "#1D3461",
                      margin: 0,
                    }}
                  >
                    Everything {vendor.vendor_name} makes,{" "}
                    <em style={{ fontStyle: "italic", color: "#C4564A" }}>on Lembra</em>.
                  </h2>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                {products.map((p) => (
                  <SkuCard
                    key={p.id}
                    sku={productToSkuCard(p, vendor.vendor_name)}
                    variant="grid"
                    onView={checkout.handleViewCard}
                  />
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ── CTA ── */}
        {!isPaused && (
          <section
            className="px-6 md:px-8 text-center"
            style={{
              background: "#1D3461",
              color: "#FAF6F0",
              paddingTop: "clamp(56px, 7vw, 80px)",
              paddingBottom: "clamp(56px, 7vw, 80px)",
            }}
          >
            <div style={{ maxWidth: 720, margin: "0 auto" }}>
              <div
                aria-hidden="true"
                className="mx-auto mb-7"
                style={{ width: 48, height: 2, background: "#C4564A", borderRadius: 999 }}
              />
              <h2
                style={{
                  fontFamily: displayFamily,
                  fontWeight: 500,
                  fontSize: "clamp(2rem, 4vw, 2.8rem)",
                  lineHeight: 1.08,
                  letterSpacing: "-0.018em",
                  color: "#FAF6F0",
                  margin: "0 auto 14px",
                  maxWidth: "20ch",
                }}
              >
                Ready to send something{" "}
                <em style={{ fontStyle: "italic", color: "#F7E8A4" }}>from them</em>?
              </h2>
              <p
                style={{
                  fontFamily: sansFamily,
                  fontSize: 17,
                  lineHeight: 1.55,
                  color: "rgba(250,246,240,0.72)",
                  margin: "0 auto 32px",
                  maxWidth: "44ch",
                }}
              >
                Tell our concierge who's celebrating. We coordinate {vendor.vendor_name},
                sign the card, and book the delivery.
              </p>
              <Link
                to={`/chat?category=${encodeURIComponent(primaryCategory)}&brief=${encodeURIComponent(`Send a gift from ${vendor.vendor_name}`)}`}
                className="inline-flex items-center gap-2 rounded-full bg-coral px-7 py-3.5 text-sm font-medium text-white transition-all hover:bg-coral/90 hover:-translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral focus-visible:ring-offset-2 focus-visible:ring-offset-navy"
                style={{
                  fontFamily: sansFamily,
                  boxShadow: "0 4px 12px -4px rgba(196,86,74,0.4)",
                }}
              >
                Send a gift
                <ArrowRight size={14} strokeWidth={2.5} />
              </Link>
            </div>
          </section>
        )}

        {/* ── Footer ── */}
        <footer style={{ background: "#1D3461", padding: "0 0 36px" }}>
          <div
            className="mx-auto px-6 md:px-8 border-t border-white/10 pt-10 flex flex-col items-center gap-3"
            style={{ maxWidth: 1180 }}
          >
            <Logo size="sm" onDark />
            <p className="text-xs text-white/50" style={{ fontFamily: sansFamily }}>
              © 2026 Lembra. All rights reserved.
            </p>
          </div>
        </footer>
      </div>

      <ProductCheckoutModals
        flow={checkout}
        isAnonymous={isAnon}
        guestSessionId={guestSessionId}
        onSubmitted={() => {
          navigate("/chat");
        }}
      />
    </>
  );
}

/* ─── Helper: shape a vendor_products row → SkuCardData ──────────── */

function productToSkuCard(p: VendorProductCard, vendorName: string): SkuCardData {
  return {
    sku_id: p.id,
    name: p.name,
    vendor_name: vendorName,
    vendor_category: p.vendor_category,
    price_aud: p.price,
    image_url: p.image_url,
    slug: p.slug,
    description: p.description,
    is_voucher: p.is_voucher,
    price_prefix_from: p.is_voucher && Boolean(p.variant_label),
    // MS312: propagate raw lead_time_days for OrderConfirmSheet
    // delivery-date validation. highlight_fields stays the
    // human-readable surface.
    lead_time_days: p.lead_time_days,
    // MS318: propagate dietary_tags for OrderConfirmSheet recipient-
    // conflict warning. Per-vendor page already selects this in
    // useVendor (MS318 update).
    dietary_tags: p.dietary_tags,
    highlight_fields:
      p.lead_time_days != null && p.lead_time_days > 0
        ? [{ label: "Lead time", value: `${p.lead_time_days} day${p.lead_time_days === 1 ? "" : "s"}` }]
        : [],
  };
}
